// ════════════════════════════════════════════════════════════════════════════
// MÃ SỰ CỐ PHẢI NẰM TRONG CÂU BÁO LỖI — KHÔNG PHẢI Ở MỘT TRƯỜNG PHỤ.
//
// 🩸 VÌ SAO CÓ FILE NÀY (04/08/2026). Trang /anomalies chết trắng vì một `Date` bị nhét thẳng
// vào câu SQL thô; bản vá xong lúc 08:27 (b06d107, mã sự cố 3693096018). Nhưng ĐÚNG sự cố ấy
// đẻ ra HAI vé, đi hai đường:
//
//   ① đường MÁY CHỦ  → vé có câu lỗi THẬT: "TypeError: The "string" argument must be of type
//                       string … Received an instance of Date"  ⇒ lần ra ngay.
//   ② đường TRÌNH DUYỆT (error boundary app/error.tsx) → vé tới nơi trong hình hài này:
//         Thông báo: "An error occurred in the Server Components render. The specific message
//                     is omitted in production builds…"   ← React tự thay, KHÔNG phải lỗi thật
//         Vết lỗi  : y hệt câu trên, KHÔNG một khung nào  ← bản production không kèm vết
//         Mã sự cố : (không thấy đâu)
//
// Vé ② KHÔNG có gì để lần: câu chữ là mẫu chung của React (trang nào chết cũng y hệt), vết rỗng.
// Thứ DUY NHẤT nối nó về vé ① và về log máy chủ là MÃ SỰ CỐ (digest) — mà mã đang được gửi ở
// trường `source`, còn thân vé thì KHÔNG in `source`. (Gói @suga-co/observe-web ghi rõ lý do:
// `source` bị cắt còn 300 chữ ở trạm nhận, nên dấu vân tay môi trường đã phải chuyển vào
// `message` — xem client.ts, chỗ dựng payload.)
//
// HẬU QUẢ ĐO ĐƯỢC: vé ② được mở lại thành MỘT VIỆC MỚI *sau khi sự cố đã vá xong*, và người
// nhận phải chẩn đoán lại từ đầu để rốt cuộc phát hiện ra là trùng. Im lặng thì không, nhưng
// "nói mà không nói gì" cũng tốn đúng một vòng công như vậy.
//
// ⇒ DÁN MÃ VÀO ĐẦU `message`. Ba lý do phải là ĐẦU chứ không phải cuối:
//   • `message` luôn được in ra thân vé (trần 1000 chữ), `source` thì không;
//   • bộ gộp-trùng của gói chung lấy dấu vân tay = `message.slice(0, 120)` — mã nằm trong 120
//     chữ đầu thì HAI sự cố khác mã mới không bị gộp làm một vé;
//   • người đọc vé (không phải dân kỹ thuật) thấy ngay "lỗi từ máy chủ", khỏi đoán.
// ════════════════════════════════════════════════════════════════════════════

/** Nhãn dán trước câu lỗi. Ngắn — vì nó ăn vào 120 chữ đầu mà bộ gộp-trùng dùng làm dấu vân tay. */
const NHAN = "lỗi từ MÁY CHỦ · mã sự cố";
/** Lời chỉ đường cho người đọc vé. Để ở CUỐI, KHÔNG nhét vào tiền tố: mọi chữ đứng trước câu gốc
 *  đều ăn vào cửa sổ 120 chữ của dấu vân tay. `digest` không phải lúc nào cũng là số ngắn — Next
 *  còn dùng chuỗi kiểu `NEXT_HTTP_ERROR_FALLBACK;404` — tiền tố dài là câu gốc rơi hết ra ngoài. */
const CHI_DUONG = "⟨câu lỗi thật nằm ở log máy chủ⟩";

/**
 * Câu báo lỗi gửi về hộp lỗi: dán MÃ SỰ CỐ lên đầu để còn tra được log máy chủ.
 * Không có mã (= lỗi phát sinh ngay ở trình duyệt) → giữ NGUYÊN câu cũ, không thêm chữ nào.
 * THUẦN: không React, không DOM → test rà thẳng được.
 */
export function cauBaoLoiCoMaSuCo(message: string | undefined | null, digest: unknown): string {
  const cau = String(message ?? "").trim();
  const ma = String(digest ?? "").trim();
  if (!ma) return cau;
  if (cau.includes(`${NHAN} ${ma}`)) return cau; // đã dán rồi (boundary render lại) → đừng dán chồng
  return `⟨${NHAN} ${ma}⟩ ${cau} ${CHI_DUONG}`.trim();
}

/**
 * Lỗi để GỬI về hộp lỗi từ error boundary: y như lỗi gốc, chỉ thêm mã sự cố vào câu.
 *
 * Giữ NGUYÊN `name`, `stack` VÀ `digest` gốc — bộ gộp-trùng lấy dấu vân tay từ KHUNG ĐẦU của vết,
 * thay vết là mỗi lượt nổ lại đẻ một vé mới; còn `digest` phải đi theo object vì đây là bản THAY
 * THẾ lỗi gốc, ai đọc `.digest` sau ta (reporter khác, log, người gọi lại) không được mất mã.
 * Không có mã thì trả về CHÍNH object gốc (không đổi gì cả).
 *
 * Nhận `unknown`: thứ tới boundary KHÔNG chắc là `Error` (giá trị `throw` trần, payload lỗi dựng
 * lại từ flight). Vật thể thường vẫn có `.message` — phải đọc nó, chứ `String(obj)` ra đúng
 * "[object Object]" là mất sạch câu lỗi.
 */
export function loiKemMaSuCo(error: unknown): Error {
  const ma = (error as { digest?: unknown } | null | undefined)?.digest;
  const goc =
    error instanceof Error
      ? error
      : new Error(String((error as { message?: unknown } | null | undefined)?.message ?? error ?? "Lỗi không rõ"));
  const cau = cauBaoLoiCoMaSuCo(goc.message, ma);
  if (cau === goc.message) return goc;
  const ve: Error & { digest?: unknown } = new Error(cau);
  ve.name = goc.name || "Error";
  if (goc.stack) ve.stack = goc.stack;
  if (ma != null) ve.digest = ma;
  return ve;
}
